import { useRef, useState, useEffect, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Stars, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import { gsap } from 'gsap';
import './Contact.css';

const LOCATIONS = [
  { name: 'Chennai', state: 'Tamil Nadu', lat: 13.02, lng: 80.20, address: 'Shop 138, Chandresen Building, Balaji Nagar, Ekkatutangal, Chennai', phone: '+91 44 2830 0000', email: 'info@csolar.cc', hours: 'Mon–Sat 9AM–6PM', maps: 'https://maps.google.com/?q=Balaji+Nagar,+Ekkatuthangal,+Chennai' },
  { name: 'Delhi', state: 'Delhi NCR', lat: 28.54, lng: 77.25, address: '51, Market 1, CR Park, New Delhi 110019', phone: '+91 11 4700 0000', email: 'info@csolar.cc', hours: 'Mon–Sat 9AM–7PM', maps: 'https://maps.google.com/?q=Market+1,+CR+Park,+New+Delhi+110019' },
];

const R = 2;

const EARTH_TEXTURES = [
  'https://unpkg.com/three-globe/example/img/earth-day.jpg',
  'https://unpkg.com/three-globe/example/img/earth-night.jpg',
  'https://unpkg.com/three-globe/example/img/earth-water.png',
  'https://unpkg.com/three-globe/example/img/earth-topology.png',
];
// Warm the texture cache as soon as the module loads (network only — no render
// cost) so the globe is ready to spin the instant the user reaches Contact.
useTexture.preload(EARTH_TEXTURES);

// The camera sits on +Z looking at the origin, so whatever point we want facing
// the viewer must be rotated to +Z.
//   • Y rotation brings India's longitude (~80°E, centroid of the 4 cities) to
//     the +Z meridian:  rotY = (270 - 80)° = 190°.
//   • X rotation tilts the globe forward by India's latitude (~23°N) so India
//     lands at screen-centre rather than up near the top:  rotX = +23° ≈ 0.40 rad.
const TARGET_ROT_Y = (270 - 80) * (Math.PI / 180);   // 190°
const TARGET_ROT_X = 23 * (Math.PI / 180);           // ~0.40 rad
const START_ROT_Y = TARGET_ROT_Y - Math.PI * 4;     // 2 full spins before settling

function latLngToVec3(lat, lng, r) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

// ── Earth ──
// This component only renders once its textures have loaded (it suspends until
// then), so the globe group is guaranteed to exist here. Driving the spin from
// inside Earth avoids the race where the animation is requested before the mesh
// exists and is silently skipped.
function Earth({ globeRef, ready, onPinClick }) {
  const [day, , clouds, normal] = useTexture(EARTH_TEXTURES);

  // Park the globe at its pre-spin pose the moment it mounts
  useEffect(() => {
    if (globeRef.current) globeRef.current.rotation.set(0, START_ROT_Y, 0);
  }, [globeRef]);

  // Spin fast, then decelerate and lock onto India — once in view
  useEffect(() => {
    if (!ready || !globeRef.current) return;
    gsap.to(globeRef.current.rotation, {
      y: TARGET_ROT_Y,
      x: TARGET_ROT_X,
      duration: 4,
      ease: 'power3.out',
    });
  }, [ready, globeRef]);

  return (
    <group ref={globeRef}>
      <mesh>
        <sphereGeometry args={[R, 64, 64]} />
        <meshPhongMaterial
          map={day}
          normalMap={normal}
          specularMap={clouds}
          specular={new THREE.Color(0x333333)}
          shininess={15}
        />
      </mesh>

      {/* Clouds */}
      <mesh>
        <sphereGeometry args={[R * 1.005, 64, 64]} />
        <meshPhongMaterial map={clouds} transparent opacity={0.22} depthWrite={false} />
      </mesh>

      {/* Atmosphere */}
      <mesh>
        <sphereGeometry args={[R * 1.06, 64, 64]} />
        <meshPhongMaterial color={0x3399ff} transparent opacity={0.07} side={THREE.FrontSide} depthWrite={false} />
      </mesh>

      {LOCATIONS.map((loc, i) => (
        <Pin key={i} loc={loc} onPinClick={onPinClick} />
      ))}
    </group>
  );
}

// ── Pin ──
function Pin({ loc, onPinClick }) {
  const groupRef = useRef();
  const ringRef = useRef();
  const [hovered, setHovered] = useState(false);
  const { camera, size } = useThree();
  const pos = latLngToVec3(loc.lat, loc.lng, R + 0.04);
  const normal = pos.clone().normalize();
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);

  useFrame(({ clock }) => {
    if (!ringRef.current) return;
    const s = 1 + 0.4 * Math.sin(clock.elapsedTime * 2.5 + loc.lat);
    ringRef.current.scale.set(s, s, s);
    ringRef.current.material.opacity = 0.55 * (0.5 + 0.5 * Math.sin(clock.elapsedTime * 2.5 + loc.lat));
  });

  // Project the pin's world position onto the canvas so the DOM can draw a
  // connector line from it to the info panel.
  const handleClick = e => {
    e.stopPropagation();
    const world = new THREE.Vector3();
    groupRef.current.getWorldPosition(world);
    const p = world.project(camera);
    onPinClick(loc, {
      x: (p.x * 0.5 + 0.5) * size.width,
      y: (-p.y * 0.5 + 0.5) * size.height,
    });
  };

  return (
    <group ref={groupRef} position={pos}>
      <mesh
        onPointerOver={e => { e.stopPropagation(); setHovered(true); }}
        onPointerOut={() => setHovered(false)}
        onClick={handleClick}
      >
        <sphereGeometry args={[hovered ? 0.05 : 0.032, 14, 14]} />
        <meshBasicMaterial color={hovered ? '#ffd074' : '#ffb347'} />
      </mesh>
      <mesh ref={ringRef} quaternion={quat}>
        <ringGeometry args={[0.055, 0.072, 32]} />
        <meshBasicMaterial color="#ff9d1e" transparent opacity={0.6} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
    </group>
  );
}

function CameraZoom({ ready }) {
  const { camera } = useThree();
  useEffect(() => {
    if (!ready) return;
    camera.position.set(0, 0, 14);
    camera.lookAt(0, 0, 0);
    gsap.to(camera.position, {
      z: 6, y: 0,
      duration: 3.4,
      ease: 'power3.out',
      onUpdate: () => camera.lookAt(0, 0, 0),
    });
  }, [ready, camera]);
  return null;
}

// ── Scene ──
function Scene({ ready, onPinClick }) {
  const globeRef = useRef();

  return (
    <>
      <Stars radius={300} depth={60} count={2500} factor={4} fade speed={0.4} />
      <ambientLight intensity={0.45} />
      <directionalLight position={[5, 3, 5]} intensity={1.6} />
      <directionalLight position={[-5, -3, -5]} intensity={0.2} />
      <CameraZoom ready={ready} />
      <Suspense fallback={null}>
        <Earth globeRef={globeRef} ready={ready} onPinClick={onPinClick} />
      </Suspense>
    </>
  );
}

// ── Connector ──
// Draws an SVG line from the clicked pin (fixed screen point) to the info
// panel's left edge, following the panel while it slides in.
function Connector({ from, panelRef, sectionRef }) {
  const lineRef = useRef();

  useEffect(() => {
    const measure = () => {
      const s = sectionRef.current?.getBoundingClientRect();
      const p = panelRef.current?.getBoundingClientRect();
      if (!s || !p || !lineRef.current) return;
      lineRef.current.setAttribute('x2', p.left - s.left);
      lineRef.current.setAttribute('y2', p.top - s.top + p.height / 2);
    };
    // Track the panel through its slide-in transition, then settle.
    const start = performance.now();
    let raf;
    const loop = () => {
      measure();
      if (performance.now() - start < 900) raf = requestAnimationFrame(loop);
    };
    loop();
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [from, panelRef, sectionRef]);

  return (
    <svg className="pin-connector" aria-hidden="true">
      <line ref={lineRef} x1={from.x} y1={from.y} x2={from.x} y2={from.y} />
      <circle cx={from.x} cy={from.y} r="3.5" />
      <circle className="pin-connector-pulse" cx={from.x} cy={from.y} r="3.5" />
    </svg>
  );
}

// ── Main ──
export default function Contact() {
  const sectionRef = useRef(null);
  const panelRef = useRef(null);
  const titleRef = useRef(null);
  const hintRef = useRef(null);
  const [mounted, setMounted] = useState(false); // mount the WebGL canvas
  const [ready, setReady] = useState(false); // start the spin
  const [panel, setPanel] = useState(null);
  const [pinPt, setPinPt] = useState(null);   // clicked pin's screen point

  const openPanel = (loc, pt) => { setPanel(loc); setPinPt(pt); };

  useEffect(() => {
    let didMount = false, didStart = false;
    // Scroll-position based trigger — robust across scroll-snap / all browsers.
    // The heavy WebGL globe is only mounted once the section nears the viewport
    // (so it never renders — and never janks — while the user is on earlier
    // sections). The spin then starts once the section is properly in view, so
    // the spin-and-settle onto India is actually witnessed.
    const onScroll = () => {
      if (!sectionRef.current) return;
      const r = sectionRef.current.getBoundingClientRect();
      const vis = (Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0)) / window.innerHeight;

      if (!didMount && vis >= 0.25) { didMount = true; setMounted(true); }

      if (!didStart && vis >= 0.55) {
        didStart = true;
        setReady(true);
        // Title + pins fade in as the globe finishes settling on India
        gsap.to(titleRef.current, { opacity: 1, duration: 1, ease: 'power2.out', delay: 3.4 });
        gsap.to(hintRef.current, { opacity: 1, duration: 1, ease: 'power2.out', delay: 3.8 });
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // in case the section is already in view on mount
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="section contact-section" id="contact" ref={sectionRef}>
      {mounted ? (
        <Canvas
          camera={{ fov: 45, near: 0.1, far: 1000, position: [0, -2.2, 14] }}
          gl={{ antialias: true, alpha: true }}
          style={{ position: 'absolute', inset: 0, zIndex: 1 }}
        >
          <Scene ready={ready} onPinClick={openPanel} />
        </Canvas>
      ) : (
        <div className="globe-placeholder" />
      )}

      <div className="contact-vignette" />

      <div className="contact-title" ref={titleRef}>
        <p className="eyebrow contact-eyebrow">Our Presence</p>
        <h1>India<br /><span className="light">Network</span></h1>
        <p className="contact-hint">Click a pin<br />to view office details.</p>
      </div>

      <div className="pin-hint" ref={hintRef}>
        <div className="hint-line" />
        Click a pin
        <div className="hint-line" />
      </div>

      {panel && pinPt && (
        <Connector from={pinPt} panelRef={panelRef} sectionRef={sectionRef} />
      )}

      {panel && (
        <div className="info-panel open" ref={panelRef}>
          <div className="panel-head">
            <div>
              <div className="panel-city">{panel.name}</div>
              <span className="panel-state">{panel.state}</span>
            </div>
            <button className="close-btn" onClick={() => { setPanel(null); setPinPt(null); }}>✕</button>
          </div>
          <div className="info-row"><span className="info-label">Address</span><span className="info-value">{panel.address}</span></div>
          <div className="info-row"><span className="info-label">Phone</span><span className="info-value">{panel.phone}</span></div>
          <div className="info-row"><span className="info-label">Email</span><span className="info-value">{panel.email}</span></div>
          <div className="info-row"><span className="info-label">Hours</span><span className="info-value">{panel.hours}</span></div>
          <div className="panel-divider" />
          <a href={panel.maps} className="maps-link" target="_blank" rel="noreferrer">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            View on Google Maps
          </a>
        </div>
      )}
    </section>
  );
}
